// Local-time date helpers (avoid UTC shifting from new Date('YYYY-MM-DD'))

export const toYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const parseYMD = (ymd) => new Date(`${ymd}T00:00:00`);

export const todayYMD = () => toYMD(new Date());

export const addDaysYMD = (ymd, days) => {
  const d = parseYMD(ymd);
  d.setDate(d.getDate() + days);
  return toYMD(d);
};

// Returns [Fri, Sat, Sun] YMD strings for the current/upcoming weekend
export const weekendYMDs = () => {
  const today = new Date();
  const dow = today.getDay(); // 0 Sun .. 6 Sat
  const offsetToFri = dow === 0 ? -2 : 5 - dow; // Mon(1)->4, Fri(5)->0, Sat(6)->-1, Sun(0)->-2
  const fri = new Date(today);
  fri.setDate(today.getDate() + offsetToFri);
  const sat = new Date(fri);
  sat.setDate(fri.getDate() + 1);
  const sun = new Date(fri);
  sun.setDate(fri.getDate() + 2);
  return [toYMD(fri), toYMD(sat), toYMD(sun)];
};

export const arraysEqual = (a, b) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

// Sort order (legacy — kept for ActivityDetailPage prev/next nav):
//  0. Ongoing with NO dates (is_ongoing && !date) → always current, sorted by name
//  1. Currently-happening (start < today ≤ end, or ongoing+only-end-date) → nearest end-date first
//  2. Upcoming (start ≥ today)                 → nearest start-date first (ascending date)
//  3. Past (fully ended)                       → nearest past first (descending date)
export const sortActivitiesByRelevance = (list) => {
  const today = todayYMD();

  // Group 0: is_ongoing with no dates at all – always "now"
  const ongoingNoDates = list
    .filter((a) => a.is_ongoing && !a.date && !a.date_end)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Group 1: currently happening (started but not yet ended)
  const ongoing = list
    .filter((a) => {
      if (a.is_ongoing && !a.date && a.date_end) return a.date_end >= today;
      if (a.is_ongoing && a.date && a.date_end) return a.date <= today && a.date_end >= today;
      return a.date && a.date < today && a.date_end && a.date_end >= today;
    })
    .sort((a, b) => (a.date_end || '').localeCompare(b.date_end || ''));

  // Group 2: upcoming
  const upcoming = list
    .filter((a) => a.date && a.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));

  // Group 3: past — everything else
  const seenIds = new Set([
    ...ongoingNoDates.map((a) => a.id),
    ...ongoing.map((a) => a.id),
    ...upcoming.map((a) => a.id),
  ]);
  const past = list
    .filter((a) => !seenIds.has(a.id))
    .sort((a, b) => {
      const ad = a.date_end || a.date || '';
      const bd = b.date_end || b.date || '';
      return bd.localeCompare(ad);
    });

  return [...ongoingNoDates, ...ongoing, ...upcoming, ...past];
};

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Haversine distance between two lat/lng points. Returns km.
// ─────────────────────────────────────────────────────────────────────────────
const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
};

// ─────────────────────────────────────────────────────────────────────────────
// Compute base score for an activity (Time Urgency + Social Proof + Admin Boost).
// Used by the admin panel to show a score breakdown without user context.
// ─────────────────────────────────────────────────────────────────────────────
export const computeBaseScore = (activity) => {
  const msPerDay = 1000 * 60 * 60 * 24;
  const nowMs = Date.now();

  // New time-urgency helper for past activities
  const pastTimeUrgency = (daysAgo) => {
    if (daysAgo <= 7) return 0;
    if (daysAgo <= 15) return -20;
    if (daysAgo <= 30) return -40;
    const extra = Math.ceil((daysAgo - 30) / 15);
    return -40 - (20 * extra);
  };

  let timeUrgency = 0;

  // Ongoing activity with no start date: always active
  if (activity.is_ongoing && !activity.date) {
    if (!activity.date_end) {
      timeUrgency = 100; // indefinite ongoing
    } else {
      const endMs = new Date(`${activity.date_end}T00:00:00`).getTime();
      if (nowMs < endMs) {
        timeUrgency = 100;
      } else {
        const daysAgo = (nowMs - endMs) / msPerDay;
        timeUrgency = pastTimeUrgency(daysAgo);
      }
    }
  } else {
    const endDate = activity.date_end || activity.date;
    const startTime = activity.start_time || '00:00';
    const activityStartMs = new Date(`${activity.date}T${startTime}:00`).getTime();
    const activityEndMs = new Date(`${endDate}T${startTime}:00`).getTime();

    const isPast = nowMs >= activityEndMs;
    const isOngoing = !isPast && activity.date_end && activity.date_end !== activity.date && nowMs >= activityStartMs;

    if (isOngoing) {
      timeUrgency = 100;
    } else if (isPast) {
      const daysAgo = (nowMs - activityEndMs) / msPerDay;
      timeUrgency = pastTimeUrgency(daysAgo);
    } else {
      const daysUntil = (activityStartMs - nowMs) / msPerDay;
      if (daysUntil <= 2) timeUrgency = 100;
      else if (daysUntil <= 7) timeUrgency = 80;
      else if (daysUntil <= 14) timeUrgency = 50;
      else if (daysUntil <= 30) timeUrgency = 30;
      else timeUrgency = 20;
    }
  }

  let socialProof = 0;
  socialProof += Math.min((activity.interested_count || 0) * 2, 40);
  socialProof += Math.min((activity.recent_interest_count || 0) * 3, 30);

  const adminBoost = activity.admin_score_boost || 0;

  return {
    time_urgency: timeUrgency,
    social_proof: socialProof,
    admin_boost: adminBoost,
    total_base_score: timeUrgency + socialProof + adminBoost,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Full scored sort for the Activities feed.
//
// context = {
//   sharedActivityId:            string | null   — activity forced to score 999
//   sharedActivityTags:          string[]        — tags of shared activity (for related boost)
//   sharedActivityChangemakerId: string | null   — changemaker of shared activity
//   utmSource:                   string | null   — from sessionStorage karmana_utm
//   utmContent:                  string | null   — from sessionStorage karmana_utm (slug or single tag)
//   utmTerm:                     string | null   — comma-separated ad tags e.g. "tree-plantation,cleanup-drive"
//   userType:                    string | null   — 'professional' | 'ngo_worker' | ...
//   userLocation:                string | null   — user's Bengaluru locality name
//   userCoords:                  {lat, lng} | null — coordinates of user's locality
//   userInterestFreqMap:         {[tagId]: weight} — interested×1, attended×2
//   userProfileInterests:        string[]          — user.interests from profile
//   categoryParentMap:           {[subCatId]: parentCatId} — built from /categories
//   likedChangemakerIds:         string[]
//   prevInterestedChangemakerIds: string[]
// }
//
// Score components:
//  Entry point signals
//   999     Shared specific activity (hard override — position 1)
//   +60     Activity shares tags with the shared activity
//   +40     Activity is by the same changemaker as shared activity
//   +70     utm_content matches activity slug
//   +50     utm_content matches an interest tag on this activity
//
//  Time Urgency (every user)
//   +100    Ongoing today OR starting 0–2 days away
//    +80    Starting 3–7 days away
//    +50    Starting 8–14 days away
//    +30    Starting 15–30 days away
//    +20    Starting 31+ days away
//    -20    Ended 0–7 days ago
//    -50    Ended 8–30 days ago
//    -80    Ended 31+ days ago
//
//  Social Proof (every user)
//   +2/click  Total interested count, capped at +40
//   +3/click  Interested clicks in last 48h (cached), capped at +30
//
//  Stated Preferences (if preference data exists)
//   +30     Exact area / locality match (or ≤10 km via Haversine)
//   +10     10–20 km via Haversine
//   +10     Working Professional + activity is on a weekend (Fri/Sat/Sun)
//   +10     NGO Worker + activity is on a weekday (Mon–Thu)
//
//  Signed-up Personalisation (if authenticated with history)
//    +5     Top-frequency tag match (freq map: interested×1, attended×2)
//    +2     Each secondary tag match
//   +10     Activity by a changemaker user has liked/followed
//   +10     Activity by a changemaker the user previously marked Interested in
//
//  Admin Boost
//   +N      admin_score_boost field (additive, any integer)
//
//  Placement constraints (noConstraints=false only)
//   Position 1: never a past activity (when ≥1 future activity exists)
//   Top 5:  max 1 past activity  (only when ≥4 future activities exist)
//   Top 10: max 2 past activities (only when ≥8 future activities exist)
// ─────────────────────────────────────────────────────────────────────────────
export const scoreAndSortActivities = (list, context = {}, { noConstraints = false } = {}) => {
  const today = todayYMD();
  const msPerDay = 1000 * 60 * 60 * 24;
  const nowMs = Date.now();

  const {
    sharedActivityId = null,
    sharedActivityTags = [],
    sharedActivityChangemakerId = null,
    utmSource: _utmSource = null, // eslint-disable-line no-unused-vars
    utmContent = null,
    utmTerm = null,
    interestNameToIdMap = {},
    userType = null,
    userLocation = null,
    userCoords = null,
    userInterestFreqMap = {},
    userProfileInterests = [],
    categoryParentMap = {},
    likedChangemakerIds = [],
    prevInterestedChangemakerIds = [],
  } = context;

  const isPastActivity = (a) => {
    // Ongoing with no end date: never past
    if (a.is_ongoing && !a.date_end) return false;
    // Ongoing with only end date: past after end date
    if (a.is_ongoing && !a.date && a.date_end) {
      return nowMs >= new Date(`${a.date_end}T00:00:00`).getTime();
    }
    const endDate = a.date_end || a.date;
    if (!endDate) return false;
    const startTime = a.start_time || '00:00';
    const activityEndMs = new Date(`${endDate}T${startTime}:00`).getTime();
    return nowMs >= activityEndMs;
  };

  const scoreActivity = (a) => {
    // Shared activity: forced to score 999 — always position 1
    if (sharedActivityId && a.id === sharedActivityId) return 999;

    const actTags = (a.areas_of_interest || []).map(String);
    let score = 0;

    // ── Entry Point Signals ──
    if (sharedActivityId) {
      if (sharedActivityTags.length > 0 && actTags.some((t) => sharedActivityTags.includes(t))) score += 60;
      if (sharedActivityChangemakerId && a.changemaker_id === sharedActivityChangemakerId) score += 40;
    }
    if (utmContent) {
      if (a.slug && utmContent === a.slug) score += 70;
      // utmContent may be a tag name — resolve to ID for comparison
      const utmContentId = interestNameToIdMap[utmContent.toLowerCase()] || utmContent;
      if (actTags.includes(utmContentId)) score += 50;
    }
    if (utmTerm) {
      // utm_term is comma-separated tag names; resolve each to its ID
      const termTags = utmTerm.split(',').map((t) => t.trim()).filter(Boolean);
      termTags.forEach((t) => {
        const id = interestNameToIdMap[t.toLowerCase()] || t;
        if (actTags.includes(id)) score += 50;
      });
    }

    // ── Time Urgency ──
    // Special case: ongoing with no start date
    if (a.is_ongoing && !a.date) {
      if (!a.date_end) {
        score += 100; // indefinite ongoing: always active
      } else {
        const endMs = new Date(`${a.date_end}T00:00:00`).getTime();
        if (nowMs < endMs) {
          score += 100;
        } else {
          const daysAgo = (nowMs - endMs) / msPerDay;
          if (daysAgo <= 7) score -= 20;
          else if (daysAgo <= 30) score -= 50;
          else score -= 80;
        }
      }
    } else {
      const endDate = a.date_end || a.date;
      const startTime = a.start_time || '00:00';
      const activityStartMs = new Date(`${a.date}T${startTime}:00`).getTime();
      const activityEndMs = new Date(`${endDate}T${startTime}:00`).getTime();

      const isPast = nowMs >= activityEndMs;
      // Multi-day event: started but end date+time hasn't arrived yet
      const isOngoing = !isPast && a.date_end && a.date_end !== a.date && nowMs >= activityStartMs;

      if (isOngoing) {
        score += 100;
      } else if (isPast) {
        const daysAgo = (nowMs - activityEndMs) / msPerDay;
        if (daysAgo <= 7) score -= 20;
        else if (daysAgo <= 30) score -= 50;
        else score -= 80;
      } else {
        const daysUntil = (activityStartMs - nowMs) / msPerDay;
        if (daysUntil <= 2) score += 100;
        else if (daysUntil <= 7) score += 80;
        else if (daysUntil <= 14) score += 50;
        else if (daysUntil <= 30) score += 30;
        else score += 20;
      }
    }

    // ── Social Proof ──
    score += Math.min((a.interested_count || 0) * 2, 40);
    score += Math.min((a.recent_interest_count || 0) * 3, 30);

    // ── Stated Preferences — Location ──
    if (userCoords && a.location?.latitude != null && a.location?.longitude != null) {
      const dist = haversineKm(userCoords.lat, userCoords.lng, a.location.latitude, a.location.longitude);
      if (dist <= 10) score += 30;
      else if (dist <= 20) score += 10;
    } else if (userLocation && a.location?.locality) {
      // Fallback: exact locality name match when coords not yet set
      if (a.location.locality === userLocation) score += 30;
    }
    if (userType === 'professional') {
      if (a.date) {
        const dow = new Date(`${a.date}T00:00:00`).getDay();
        if (dow === 0 || dow === 5 || dow === 6) score += 10;
      }
    }
    if (userType === 'ngo_worker') {
      if (a.date) {
        const dow = new Date(`${a.date}T00:00:00`).getDay();
        if (dow >= 1 && dow <= 4) score += 10;
      }
    }

    // ── Signed-up Personalisation ──
    if (a.changemaker_id && likedChangemakerIds.includes(a.changemaker_id)) score += 10;
    if (a.changemaker_id && prevInterestedChangemakerIds.includes(a.changemaker_id)) score += 10;

    const freqEntries = Object.entries(userInterestFreqMap).sort((x, y) => y[1] - x[1]);
    if (freqEntries.length > 0) {
      const topTag = freqEntries[0][0];
      const secondaryTags = freqEntries.slice(1).map(([t]) => t);
      if (actTags.includes(topTag)) score += 5;
      secondaryTags.forEach((t) => { if (actTags.includes(t)) score += 2; });
    }

    // ── Sibling Boost: same parent category affinity ──
    // Activities tagged with a sub-category that shares the same parent as a
    // user interest (freq map or profile) get +1 per matching sibling tag.
    if (Object.keys(categoryParentMap).length > 0) {
      const userInterestParents = new Set();
      Object.keys(userInterestFreqMap).forEach((t) => {
        const p = categoryParentMap[t]; if (p) userInterestParents.add(p);
      });
      userProfileInterests.forEach((t) => {
        const p = categoryParentMap[t]; if (p) userInterestParents.add(p);
      });
      if (userInterestParents.size > 0) {
        actTags.forEach((t) => {
          const p = categoryParentMap[t];
          if (p && userInterestParents.has(p) && !userInterestFreqMap[t]) score += 1;
        });
      }
    }

    // ── Admin Boost (additive) ──
    score += (a.admin_score_boost || 0);

    return score;
  };

  // 1. Pre-compute each activity's score exactly once, filter to >= -20, sort descending.
  //    Computing scoreActivity repeatedly inside sort() comparators can produce inconsistent
  //    results for floating-point day differences — pre-computing guarantees a stable sort.
  const withScores = list
    .map((a) => ({ activity: a, score: scoreActivity(a) }))
    .filter(({ score }) => score >= -20);
  withScores.sort((a, b) => b.score - a.score);
  const sorted = withScores.map(({ activity }) => activity);

  // When showing past-only view, no placement constraints needed — just return sorted
  if (noConstraints) return sorted;

  // 2. Apply placement constraints:
  //    - Top 5:  max 1 past activity  (only enforced when ≥4 future activities exist)
  //    - Top 10: max 2 past activities (only enforced when ≥8 future activities exist)
  //    Constraints are inactive when there aren't enough future activities to fill the slots.
  const futureCount = sorted.filter((a) => !isPastActivity(a)).length;

  const result = [];
  const deferred = [];

  for (const activity of sorted) {
    const pos = result.length + 1; // 1-indexed position about to be filled
    if (isPastActivity(activity)) {
      const pastInTop5  = result.slice(0, 5).filter(isPastActivity).length;
      const pastInTop10 = result.slice(0, 10).filter(isPastActivity).length;
      if (futureCount >= 1 && pos === 1)              { deferred.push(activity); continue; }
      if (futureCount >= 4 && pos <= 5  && pastInTop5  >= 1) { deferred.push(activity); continue; }
      if (futureCount >= 8 && pos <= 10 && pastInTop10 >= 2) { deferred.push(activity); continue; }
    }
    result.push(activity);
  }

  return [...result, ...deferred];
};

// ─────────────────────────────────────────────────────────────────────────────
// scoreActivityDetailed — same logic as scoreActivity but returns breakdown
// Returns: { total: number, breakdown: [{ label: string, score: number }] }
// ─────────────────────────────────────────────────────────────────────────────
export const scoreActivityDetailed = (a, context = {}) => {
  const today = todayYMD();
  const msPerDay = 1000 * 60 * 60 * 24;
  const nowMs = Date.now();

  const {
    sharedActivityId = null,
    sharedActivityTags = [],
    sharedActivityChangemakerId = null,
    utmContent = null,
    utmTerm = null,
    interestNameToIdMap = {},
    userType = null,
    userLocation = null,
    userCoords = null,
    userInterestFreqMap = {},
    userProfileInterests = [],
    categoryParentMap = {},
    likedChangemakerIds = [],
    prevInterestedChangemakerIds = [],
  } = context;

  if (sharedActivityId && a.id === sharedActivityId) {
    return { total: 999, breakdown: [{ label: 'Shared activity (hard override)', score: 999 }] };
  }

  const actTags = (a.areas_of_interest || []).map(String);
  const breakdown = [];
  let total = 0;

  const add = (label, value) => {
    breakdown.push({ label, score: value });
    total += value;
  };

  // ── Entry Point Signals ──
  if (sharedActivityId) {
    if (sharedActivityTags.length > 0 && actTags.some((t) => sharedActivityTags.includes(t))) add('Related tags to shared activity', 60);
    if (sharedActivityChangemakerId && a.changemaker_id === sharedActivityChangemakerId) add('Same organiser as shared activity', 40);
  }
  if (utmContent) {
    if (a.slug && utmContent === a.slug) add('UTM slug match', 70);
    const utmContentId = interestNameToIdMap[utmContent.toLowerCase()] || utmContent;
    if (actTags.includes(utmContentId)) add('UTM interest tag match', 50);
  }
  if (utmTerm) {
    const termTags = utmTerm.split(',').map((t) => t.trim()).filter(Boolean);
    const matched = termTags.filter((t) => actTags.includes(interestNameToIdMap[t.toLowerCase()] || t));
    if (matched.length > 0) add(`UTM term tag match (${matched.join(', ')})`, 50 * matched.length);
  }

  // ── Time Urgency ──
  if (a.is_ongoing && !a.date) {
    if (!a.date_end) {
      add('Ongoing (no dates – always active)', 100);
    } else {
      const endMs = new Date(`${a.date_end}T00:00:00`).getTime();
      if (nowMs < endMs) {
        add('Ongoing (active until last date)', 100);
      } else {
        const daysAgo = (nowMs - endMs) / msPerDay;
        if (daysAgo <= 7)       add('Ongoing ended 0–7 days ago', -20);
        else if (daysAgo <= 30) add('Ongoing ended 8–30 days ago', -50);
        else                    add('Ongoing ended 31+ days ago', -80);
      }
    }
  } else {
    const endDate = a.date_end || a.date;
    const startTime = a.start_time || '00:00';
    const activityStartMs = new Date(`${a.date}T${startTime}:00`).getTime();
    const activityEndMs = new Date(`${endDate}T${startTime}:00`).getTime();

    const isPast = nowMs >= activityEndMs;
    const isOngoing = !isPast && a.date_end && a.date_end !== a.date && nowMs >= activityStartMs;

    if (isOngoing) {
      add('Ongoing today', 100);
    } else if (isPast) {
      const daysAgo = (nowMs - activityEndMs) / msPerDay;
      if (daysAgo <= 7)       add('Past 0–7 days ago', -20);
      else if (daysAgo <= 30) add('Past 8–30 days ago', -50);
      else                    add('Past 31+ days ago', -80);
    } else {
      const daysUntil = (activityStartMs - nowMs) / msPerDay;
      if (daysUntil <= 2)       add('Starting 0–2 days', 100);
      else if (daysUntil <= 7)  add('Starting 3–7 days', 80);
      else if (daysUntil <= 14) add('Starting 8–14 days', 50);
      else if (daysUntil <= 30) add('Starting 15–30 days', 30);
      else                      add('Starting 31+ days', 20);
    }
  }

  // ── Social Proof ──
  const intScore = Math.min((a.interested_count || 0) * 2, 40);
  if (intScore > 0) add(`Social proof: ${a.interested_count} interested (×2, cap 40)`, intScore);
  const recentScore = Math.min((a.recent_interest_count || 0) * 3, 30);
  if (recentScore > 0) add(`Recent interest: ${a.recent_interest_count} in 48h (×3, cap 30)`, recentScore);

  // ── Stated Preferences — Location ──
  if (userCoords && a.location?.latitude != null && a.location?.longitude != null) {
    const dist = haversineKm(userCoords.lat, userCoords.lng, a.location.latitude, a.location.longitude);
    if (dist <= 10)       add(`Location: ${dist.toFixed(1)}km (≤10km)`, 30);
    else if (dist <= 20)  add(`Location: ${dist.toFixed(1)}km (10–20km)`, 10);
    else                  add(`Location: ${dist.toFixed(1)}km (>20km)`, 0);
  } else if (userLocation && a.location?.locality) {
    if (a.location.locality === userLocation) add('Location: exact locality match (no coords)', 30);
  }

  if (userType === 'professional') {
    if (a.date) {
      const dow = new Date(`${a.date}T00:00:00`).getDay();
      if (dow === 0 || dow === 5 || dow === 6) add('Professional + weekend activity', 10);
    }
  }
  if (userType === 'ngo_worker') {
    if (a.date) {
      const dow = new Date(`${a.date}T00:00:00`).getDay();
      if (dow >= 1 && dow <= 4) add('NGO Worker + weekday activity', 10);
    }
  }

  // ── Signed-up Personalisation ──
  if (a.changemaker_id && likedChangemakerIds.includes(a.changemaker_id)) add('Organiser liked/followed by user', 10);
  if (a.changemaker_id && prevInterestedChangemakerIds.includes(a.changemaker_id)) add('Organiser previously interacted with', 10);

  const freqEntries = Object.entries(userInterestFreqMap).sort((x, y) => y[1] - x[1]);
  if (freqEntries.length > 0) {
    const topTag = freqEntries[0][0];
    const secondaryTags = freqEntries.slice(1).map(([t]) => t);
    if (actTags.includes(topTag)) add(`Top interest tag match (${topTag})`, 5);
    const matchedSecondary = secondaryTags.filter((t) => actTags.includes(t));
    if (matchedSecondary.length > 0) add(`Secondary tag match (${matchedSecondary.join(', ')})`, 2 * matchedSecondary.length);
  }

  // ── Sibling Boost: same parent category affinity ──
  if (Object.keys(categoryParentMap).length > 0) {
    const userInterestParents = new Set();
    Object.keys(userInterestFreqMap).forEach((t) => { const p = categoryParentMap[t]; if (p) userInterestParents.add(p); });
    userProfileInterests.forEach((t) => { const p = categoryParentMap[t]; if (p) userInterestParents.add(p); });
    if (userInterestParents.size > 0) {
      const siblingMatches = actTags.filter((t) => {
        const p = categoryParentMap[t];
        return p && userInterestParents.has(p) && !userInterestFreqMap[t];
      });
      if (siblingMatches.length > 0) {
        add(`Category affinity boost (${siblingMatches.length} sibling tag${siblingMatches.length > 1 ? 's' : ''})`, siblingMatches.length);
      }
    }
  }

  // ── Admin Boost ──
  if ((a.admin_score_boost || 0) !== 0) add('Admin score boost', a.admin_score_boost);

  return { total, breakdown: breakdown.filter((b) => b.score !== 0) };
};

export const formatLongDate = (ymd) =>
  parseYMD(ymd).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

// Formats a date or date range for display
// e.g. "Jun 10, 2025" or "Jun 10 – Jun 12, 2025"
export const formatDateRange = (date, dateEnd) => {
  if (!date) return '';
  const fmtFull = (d) => parseYMD(d).toLocaleDateString('en-IN', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  if (!dateEnd) return fmtFull(date);
  const startShort = parseYMD(date).toLocaleDateString('en-IN', {
    month: 'short', day: 'numeric',
  });
  return `${startShort} – ${fmtFull(dateEnd)}`;
};


